import { validateRecipient } from './account-keys.js';
import { verifyPasskeyApproval } from './approval.js';
import { approvalChallengeFor } from './approval-batch.js';
import { fromBase64Url, randomBytes } from './bytes.js';
import { exactKeys } from './content.js';
import { credentialSetHash } from './credentials.js';
import { DOMAIN, hashCanonical, hashCanonicalB64 } from './hash.js';
import { isEcPublicJwk, jwkThumbprint } from './keys.js';
import { deriveSas } from './sas.js';
import { isWebAuthnAssertion } from './webauthn.js';
export const ENROLLMENT_CODE_TTL_MS = 5 * 60_000;
/**
 * Upper bound for a code's window. Setup-bound codes (`viko setup`) live as long as the
 * one-time setup link (15 min): the daemon claims them before the link is shown, so the longer
 * window only gives the user time to create the account before approving.
 */
export const MAX_ENROLLMENT_CODE_TTL_MS = 15 * 60_000;
const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
/** 8-character Crockford base32 code (40 bits), formatted `XXXX-XXXX`. */
export function generateEnrollmentCode() {
    const bytes = randomBytes(5);
    let acc = 0n;
    for (const b of bytes)
        acc = (acc << 8n) | BigInt(b);
    let out = '';
    for (let i = 7; i >= 0; i--)
        out += CROCKFORD[Number((acc >> BigInt(i * 5)) & 31n)];
    return `${out.slice(0, 4)}-${out.slice(4)}`;
}
/** Uppercase, drop separators, and map the Crockford look-alikes (O→0, I/L→1). */
export function normalizeEnrollmentCode(code) {
    return code.toUpperCase().replace(/[\s-]/g, '').replace(/O/g, '0').replace(/[IL]/g, '1');
}
/**
 * The words both screens show. Any substitution by a man-in-the-middle (daemon key, passkey
 * set, machine id, or code) changes them.
 */
export async function deriveEnrollmentSas(transcript) {
    return deriveSas({
        type: 'viko.enrollment-sas',
        machineId: transcript.machineId,
        daemonKeyId: transcript.daemonKeyId,
        credentialSetHash: transcript.credentialSetHash,
        code: normalizeEnrollmentCode(transcript.code),
    });
}
async function codeHash(code) {
    return hashCanonicalB64(DOMAIN.enrollment, { code: normalizeEnrollmentCode(code) });
}
export async function createEnrollmentStatement(input) {
    const issuedAt = input.now ?? Date.now();
    const daemonPublicKey = {
        kty: 'EC',
        crv: 'P-256',
        x: input.daemonPublicKey.x,
        y: input.daemonPublicKey.y,
    };
    const statement = {
        v: 1,
        type: 'viko.enrollment',
        machineId: input.machineId,
        machineName: input.machineName,
        daemonKeyId: await jwkThumbprint(daemonPublicKey),
        daemonPublicKey,
        credentialSetHash: await credentialSetHash(input.credentials),
        codeHash: await codeHash(input.code),
        rpId: input.rpId,
        issuedAt,
        expiresAt: issuedAt + ENROLLMENT_CODE_TTL_MS,
    };
    if (input.content)
        statement.content = structuredClone(input.content);
    return statement;
}
export async function createEnrollmentChallenge(statement) {
    return hashCanonical(DOMAIN.enrollment, statement);
}
/**
 * Daemon-side: accept the credential set relayed by the Worker only if (a) a passkey from that
 * very set approved a statement naming this daemon's key, machine and code, (b) the code is
 * still within its 5-minute window, and (c) the user confirmed the SAS locally (caller's job).
 */
export async function verifyEnrollmentApproval(approval, expected) {
    // Detach caller-owned objects before the first asynchronous verification step.
    try {
        approval = structuredClone(approval);
    }
    catch {
        return { ok: false, reason: 'malformed' };
    }
    const s = approval?.statement;
    if (typeof s !== 'object' ||
        s === null ||
        s.v !== 1 ||
        s.type !== 'viko.enrollment' ||
        !isEcPublicJwk(s.daemonPublicKey) ||
        !Number.isSafeInteger(s.issuedAt) ||
        !Number.isSafeInteger(s.expiresAt) ||
        !isWebAuthnAssertion(approval.assertion)) {
        return { ok: false, reason: 'malformed' };
    }
    const now = expected.now ?? Date.now();
    const skew = expected.clockSkewMs ?? 60_000;
    const codeTtl = Math.min(expected.codeTtlMs ?? ENROLLMENT_CODE_TTL_MS, MAX_ENROLLMENT_CODE_TTL_MS);
    if (expected.credentials.length === 0)
        return { ok: false, reason: 'credential-set-mismatch' };
    if (s.machineId !== expected.machineId)
        return { ok: false, reason: 'machine-mismatch' };
    const expectedKeyId = await jwkThumbprint({
        kty: 'EC',
        crv: 'P-256',
        x: expected.daemonPublicKey.x,
        y: expected.daemonPublicKey.y,
    });
    if (s.daemonKeyId !== expectedKeyId ||
        s.daemonPublicKey.x !== expected.daemonPublicKey.x ||
        s.daemonPublicKey.y !== expected.daemonPublicKey.y) {
        return { ok: false, reason: 'key-mismatch' };
    }
    if (s.credentialSetHash !== (await credentialSetHash(expected.credentials))) {
        return { ok: false, reason: 'credential-set-mismatch' };
    }
    if (s.codeHash !== (await codeHash(expected.code)))
        return { ok: false, reason: 'code-mismatch' };
    if (s.rpId !== expected.rp.rpId)
        return { ok: false, reason: 'rp-mismatch' };
    if (now > expected.codeIssuedAt + codeTtl + skew ||
        now > s.expiresAt + skew ||
        s.expiresAt - s.issuedAt > ENROLLMENT_CODE_TTL_MS) {
        return { ok: false, reason: 'expired' };
    }
    if (expected.content) {
        try {
            if (!(await contentGrantMatches(s, expected.machineId, expected.content)))
                return { ok: false, reason: 'content-mismatch' };
        }
        catch {
            return { ok: false, reason: 'content-mismatch' };
        }
    }
    let challenge;
    try {
        challenge = await approvalChallengeFor(DOMAIN.enrollment, await createEnrollmentChallenge(s), approval.batch);
    }
    catch {
        return { ok: false, reason: 'malformed' };
    }
    const result = await verifyPasskeyApproval(approval.assertion, challenge, {
        credentials: expected.credentials,
        rp: expected.rp,
    });
    if (!result.ok)
        return result;
    const out = {
        ok: true,
        approvedBy: result.credential.credentialId,
        credentials: [...expected.credentials],
    };
    if (expected.content && s.content)
        out.content = structuredClone(s.content);
    return out;
}
async function contentGrantMatches(s, machineId, expected) {
    const c = s.content;
    if (!c || typeof c !== 'object' || c.accountId !== expected.accountId)
        return false;
    exactKeys(c, ['accountId', 'recipient', 'epochs']);
    const r = c.recipient;
    if (r?.type !== 'daemon' || r.ref !== machineId || !isEcPublicJwk(r.publicKey))
        return false;
    await validateRecipient(r);
    const id = await jwkThumbprint({
        kty: 'EC',
        crv: 'P-256',
        x: expected.publicKey.x,
        y: expected.publicKey.y,
    });
    if (r.id !== id ||
        r.publicKey.x !== expected.publicKey.x ||
        r.publicKey.y !== expected.publicKey.y)
        return false;
    if (!Array.isArray(c.epochs) || c.epochs.length < 1 || c.epochs.length > 4096)
        return false;
    const seen = new Set();
    for (const e of c.epochs) {
        exactKeys(e, ['epoch', 'keyCommitment']);
        if (!Number.isSafeInteger(e?.epoch) || e.epoch < 1 || seen.has(e.epoch))
            return false;
        if (typeof e.keyCommitment !== 'string' || fromBase64Url(e.keyCommitment).length !== 32)
            return false;
        seen.add(e.epoch);
    }
    return true;
}
//# sourceMappingURL=enrollment.js.map